
import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Send, Bot, User, X, Play, Settings } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { useNavigate, useLocation } from 'react-router-dom';
import { useActivityContext } from '@/hooks/useActivityContext';
import { useUserPerformance } from '@/hooks/useUserPerformance';
import { ActivityResponseParser } from './ActivityResponseParser';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  recommendations?: ActivityRecommendation[];
}

interface ActivityRecommendation {
  type: 'test' | 'game' | 'worksheet' | 'writing';
  title: string;
  description: string;
  subject: string;
  grade: string;
  difficulty?: string;
  actionData: {
    subjectId?: string;
    categoryId?: string;
    subjectName: string;
    gradeName: string;
  };
}

interface ChatbotInterfaceProps {
  grade: string;
  subject?: string;
  onClose?: () => void;
}

export const ChatbotInterface: React.FC<ChatbotInterfaceProps> = ({
  grade: propGrade = '1',
  subject: propSubject,
  onClose
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { getCurrentActivityData } = useActivityContext();
  const { performance } = useUserPerformance();
  
  // Ensure subject is a string, not an object
  const subject = typeof propSubject === 'string' ? propSubject : undefined;
  
  console.log('ChatbotInterface initialized with:', { 
    propGrade, 
    propSubject, 
    subject, 
    subjectType: typeof propSubject 
  });
  
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [recentActivity, setRecentActivity] = useState<any[]>([]);
  const [selectedGrade, setSelectedGrade] = useState(propGrade);
  const [showGradeSettings, setShowGradeSettings] = useState(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const grades = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

  // Enhanced activity action handlers
  const handleStartTest = async (actionData: any) => {
    console.log('Starting test with data:', actionData);
    toast.success(`Starting ${actionData.subjectName} test for Grade ${actionData.gradeName}...`);
    
    try {
      const { data: session, error } = await supabase
        .from('test_sessions')
        .insert({
          user_id: user?.id,
          subject_id: actionData.subjectId,
          grade: actionData.gradeName,
          total_questions: 10,
          completed: false
        })
        .select()
        .single();

      if (error) throw error;

      const subjectUrl = actionData.subjectName.toLowerCase().replace(/\s+/g, '-');
      const testUrl = actionData.categoryId 
        ? `/test/${actionData.gradeName}/${subjectUrl}/${actionData.categoryId}`
        : `/test/${actionData.gradeName}/${subjectUrl}`;
      
      if (onClose) onClose();
      navigate(testUrl);
    } catch (error) {
      console.error('Error starting test:', error);
      toast.error('Failed to start test. Please try again.');
    }
  };

  const handleStartGame = (actionData: any) => {
    console.log('Starting game with data:', actionData);
    toast.success(`Opening ${actionData.subjectName} games for Grade ${actionData.gradeName}...`);
    
    const subjectUrl = actionData.subjectName.toLowerCase().replace(/\s+/g, '-');
    const gameUrl = `/games/${actionData.gradeName}/${subjectUrl}`;
    
    if (onClose) onClose();
    navigate(gameUrl);
  };

  const handleOpenWorksheet = (actionData: any) => {
    console.log('Opening worksheet with data:', actionData);
    toast.success(`Opening ${actionData.subjectName} worksheets for Grade ${actionData.gradeName}...`);
    
    const subjectUrl = actionData.subjectName.toLowerCase().replace(/\s+/g, '-');
    const worksheetUrl = `/worksheets/${actionData.gradeName}/${subjectUrl}`;
    
    if (onClose) onClose();
    navigate(worksheetUrl);
  };

  const handleStartWriting = (actionData: any) => {
    console.log('Starting writing with data:', actionData);
    toast.success(`Opening writing practice for Grade ${actionData.gradeName}...`);
    
    const writingUrl = `/writing/${actionData.gradeName}`;
    
    if (onClose) onClose();
    navigate(writingUrl);
  };

  const handleActivityAction = (recommendation: any) => {
    console.log('Activity action triggered:', recommendation);
    
    switch (recommendation.type) {
      case 'test':
        handleStartTest(recommendation.actionData);
        break;
      case 'game':
        handleStartGame(recommendation.actionData);
        break;
      case 'worksheet':
        handleOpenWorksheet(recommendation.actionData);
        break;
      case 'writing':
        handleStartWriting(recommendation.actionData);
        break;
      default:
        toast.error('Unknown activity type');
    }
  };

  useEffect(() => {
    if (user && !sessionId) {
      initializeSession();
    }
  }, [user, selectedGrade, subject, sessionId]);

  useEffect(() => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
    }
  }, [messages]);

  const loadUserContext = async () => {
    if (!user) return;

    console.log('Loading user context...');
    try {
      console.log('Fetching profile...');
      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      console.log('Profile loaded:', profile);

      console.log('Fetching test sessions...');
      const { data: testSessions } = await supabase
        .from('test_sessions')
        .select(`
          *,
          subjects(name, grade)
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(10);

      console.log('Test sessions loaded:', testSessions?.length || 0);

      console.log('Fetching game sessions...');
      const { data: gameSessions } = await supabase
        .from('game_sessions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(10);

      console.log('Game sessions loaded:', gameSessions?.length || 0);

      setUserProfile(profile);
      setRecentActivity([...(testSessions || []), ...(gameSessions || [])]);
      
      console.log('User context loaded successfully');
    } catch (error) {
      console.error('Error loading user context:', error);
      // Don't fail the entire initialization if context loading fails
      setUserProfile(null);
      setRecentActivity([]);
    }
  };

  const initializeSession = async (): Promise<string | null> => {
    console.log('Initializing session...', { user: !!user, selectedGrade, subject });
    
    if (!user) {
      console.error('No user found when initializing session');
      return null;
    }

    try {
      console.log('Loading user context...');
      await loadUserContext();
      console.log('User context loaded');

      console.log('Creating chat session...');
      const { data, error } = await supabase
        .from('chat_sessions')
        .insert({
          user_id: user.id,
          grade: selectedGrade,
          subject,
          title: `Chat about ${subject || 'General'} - Grade ${selectedGrade}`
        })
        .select()
        .single();

      console.log('Chat session response:', { data, error });

      if (error) {
        console.error('Database error creating session:', error);
        throw error;
      }

      if (!data) {
        console.error('No data returned from session creation');
        throw new Error('No session data returned');
      }

      const newId = data.id;
      console.log('New session created with ID:', newId);
      setSessionId(newId);
      
      const userName = userProfile?.first_name || user?.email?.split('@')[0] || 'there';
      let contextMessage = `Hi ${userName}! I'm your AI learning assistant. I can help you with Grade ${selectedGrade}${subject ? ` ${subject}` : ''} activities and provide personalized recommendations based on your learning progress.`;
      
      // Enhanced context based on current page and activity
      const currentPath = location.pathname;
      const isOnTest = currentPath.includes('/test/') || (currentPath.includes('/grade/') && currentPath.includes('/subject/'));
      const isInstructorPage = currentPath.includes('/instructor');
      const activityData = getCurrentActivityData();
      
      console.log('Activity data for context:', { currentPath, isOnTest, isInstructorPage, activityData });
      
      if (isInstructorPage) {
        if (currentPath.includes('/tests/') && currentPath.includes('/edit')) {
          contextMessage += " I see you're editing a test. I can help with creating questions, managing difficulty levels, and organizing test content!";
        } else if (currentPath.includes('/tests/create')) {
          contextMessage += " I see you're creating a new test. I can help suggest question types, difficulty balancing, and test structure!";
        } else if (currentPath.includes('/student-lists')) {
          contextMessage += " I see you're managing student lists. I can help with organizing students and test administration!";
        } else {
          contextMessage += " I see you're in the instructor dashboard. I can help with test creation, student management, and assessment strategies!";
        }
      } else if (isOnTest) {
        contextMessage += " I see you're working on a test. I can help explain concepts or suggest similar practice problems!";
        if (activityData.activityDetails?.quiz) {
          const quizDetails = activityData.activityDetails.quiz;
          contextMessage += ` You're currently on question ${(quizDetails.currentQuestionIndex || 0) + 1} of ${quizDetails.totalQuestions || 'several'} in your ${quizDetails.subject || 'test'}${quizDetails.category ? ` ${quizDetails.category}` : ''} assessment.`;
          if (quizDetails.currentQuestion) {
            contextMessage += " Feel free to ask me about concepts related to your current question - I can help guide your thinking without giving away answers!";
          }
        }
      } else if (currentPath.includes('/games/')) {
        contextMessage += " I see you're playing an educational game. Need help understanding any concepts?";
      } else if (currentPath.includes('/worksheets/')) {
        contextMessage += " I see you're working on a worksheet. I can help explain topics or provide additional practice!";
      } else if (currentPath.includes('/writing/')) {
        contextMessage += " I see you're working on writing. I can help with grammar, structure, or topic ideas!";
      } else if (currentPath.includes('/analysis/')) {
        contextMessage += " I can help you understand your performance analysis and suggest ways to improve!";
      }
      
      // Add performance context
      if (performance && performance.totalSessions > 0) {
        if (performance.overallAverage >= 80) {
          contextMessage += ` Great job! Your overall average is ${Math.round(performance.overallAverage)}%. You're doing excellent!`;
        } else if (performance.overallAverage >= 60) {
          contextMessage += ` Your overall average is ${Math.round(performance.overallAverage)}%. You're making good progress!`;
        } else {
          contextMessage += ` I see your overall average is ${Math.round(performance.overallAverage)}%. Let me help you improve with targeted practice!`;
        }

        if (performance.weakAreas.length > 0) {
          contextMessage += ` I notice you might want to focus more on: ${performance.weakAreas.slice(0, 2).join(', ')}.`;
        }

        if (performance.recentTrend === 'improving') {
          contextMessage += " I can see you're improving - keep up the great work!";
        } else if (performance.recentTrend === 'declining') {
          contextMessage += " Let's work together to get you back on track!";
        }
      }
      
      contextMessage += " What would you like to work on today?";
      
      const welcomeMessage: Message = {
        id: 'welcome',
        role: 'assistant',
        content: contextMessage,
        timestamp: new Date()
      };
      
      console.log('Setting welcome message and returning session ID');
      setMessages([welcomeMessage]);
      return newId;
    } catch (error) {
      console.error('Error initializing chat session:', error);
      toast.error(`Failed to start chat session: ${error.message || 'Unknown error'}`);
      return null;
    }
  };

  const handleGradeChange = async (newGrade: string) => {
    setSelectedGrade(newGrade);
    setShowGradeSettings(false);
    
    try {
      await supabase
        .from('user_learning_preferences')
        .upsert({
          user_id: user?.id,
          grade: newGrade,
          last_updated: new Date().toISOString()
        }, {
          onConflict: 'user_id,grade'
        });
    } catch (error) {
      console.error('Error saving grade preference:', error);
    }
    
    setMessages([]);
    setSessionId(null);
  };

  const sendMessage = async () => {
    console.log('SendMessage called', { input: input.trim(), user: !!user, sessionId });
    
    if (!input.trim()) {
      console.log('Empty input, returning');
      return;
    }
    
    if (!user) {
      console.log('No user, returning');
      toast.error('Please log in to use the chat assistant.');
      return;
    }

    setIsLoading(true);
    console.log('Starting to send message...');

    try {
      // Ensure a session exists
      let currentId = sessionId;
      console.log('Current session ID:', currentId);
      
      if (!currentId) {
        console.log('No session ID, creating new session...');
        currentId = await initializeSession();
        console.log('New session created:', currentId);
      }
      
      if (!currentId) {
        console.error('Failed to create or get session ID');
        toast.error('Unable to start chat session. Please try again.');
        setIsLoading(false);
        return;
      }

      const userMessage: Message = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: input,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, userMessage]);
      setInput('');

      await supabase.from('chat_messages').insert({
        session_id: currentId,
        role: 'user',
        content: userMessage.content
      });

      const conversationHistory = [...messages, userMessage].map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      // Enhanced context data
      const currentPath = location.pathname;
      const isOnTest = currentPath.includes('/test/') || (currentPath.includes('/grade/') && currentPath.includes('/subject/'));
      const isInstructorPage = currentPath.includes('/instructor');
      const activityData = getCurrentActivityData();
      
      let pageContext = '';
      if (isInstructorPage) {
        if (currentPath.includes('/tests/') && currentPath.includes('/edit')) {
          pageContext = 'The instructor is editing a test';
        } else if (currentPath.includes('/tests/create')) {
          pageContext = 'The instructor is creating a new test';
        } else if (currentPath.includes('/student-lists')) {
          pageContext = 'The instructor is managing student lists';
        } else {
          pageContext = 'The instructor is in the instructor dashboard';
        }
      } else if (isOnTest) {
        pageContext = 'The user is currently taking a test';
        const quiz = (activityData as any)?.activityDetails?.quiz;
        if (quiz) {
          pageContext += ` (Question ${(quiz.currentQuestionIndex ?? 0) + 1} of ${quiz.totalQuestions ?? 'unknown'})`;
        }
      } else if (currentPath.includes('/games/')) {
        pageContext = 'The user is currently playing an educational game';
      } else if (currentPath.includes('/worksheets/')) {
        pageContext = 'The user is currently working on a worksheet';
      } else if (currentPath.includes('/writing/')) {
        pageContext = 'The user is currently working on a writing assignment';
      } else if (currentPath.includes('/analysis/')) {
        pageContext = 'The user is viewing their performance analysis';
      } else if (currentPath.includes('/grade/')) {
        pageContext = 'The user is selecting subjects and activities for their grade level';
      }

      const userContext = {
        profile: userProfile,
        recentActivity: recentActivity,
        currentPage: pageContext,
        selectedGrade: selectedGrade,
        activityData: activityData,
        performance: performance
      };

      console.log('Calling chatbot-assistant function with:', {
        message: userMessage.content,
        sessionId: currentId,
        grade: selectedGrade,
        subject,
        userContext
      });

      const { data, error } = await supabase.functions.invoke('chatbot-assistant', {
        body: {
          message: userMessage.content,
          sessionId: currentId,
          grade: selectedGrade,
          subject,
          conversationHistory,
          pageContext,
          userContext,
          userId: user.id
        }
      });

      console.log('Function response:', { data, error });

      if (error) {
        console.error('Supabase function error:', error);
        throw error;
      }

      const assistantMessage: Message = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: data.message,
        timestamp: new Date(),
        recommendations: data.recommendations || []
      };

      setMessages(prev => [...prev, assistantMessage]);

      await supabase.from('chat_messages').insert({
        session_id: currentId,
        role: 'assistant',
        content: data.message,
        message_type: data.recommendations?.length > 0 ? 'activity_recommendation' : 'text',
        metadata: { recommendations: data.recommendations }
      });

      await supabase
        .from('chat_sessions')
        .update({ last_activity: new Date().toISOString() })
        .eq('id', currentId);

    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
      
      const errorMessage: Message = {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: "I'm sorry, I'm having trouble right now. Please try again later.",
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    console.log('Key pressed:', e.key, 'shiftKey:', e.shiftKey);
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      console.log('Enter pressed, calling sendMessage');
      sendMessage();
    }
  };

  return (
    <div className="flex flex-col h-full max-h-[600px] bg-background border rounded-lg shadow-lg">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <div className="flex items-center gap-2">
          <Bot className="h-5 w-5 text-primary" />
          <div>
            <h3 className="font-semibold">AI Learning Assistant</h3>
            <p className="text-xs text-muted-foreground">
              Grade {selectedGrade} {subject && `• ${subject}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowGradeSettings(!showGradeSettings)}
          >
            <Settings className="h-4 w-4" />
          </Button>
          {onClose && (
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Grade Settings */}
      {showGradeSettings && (
        <div className="p-4 border-b bg-muted/50">
          <div className="space-y-2">
            <label className="text-sm font-medium">Select Grade:</label>
            <Select value={selectedGrade} onValueChange={handleGradeChange}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select grade" />
              </SelectTrigger>
              <SelectContent>
                {grades.map((grade) => (
                  <SelectItem key={grade} value={grade}>
                    Grade {grade}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* Messages */}
      <ScrollArea className="flex-1 p-4" ref={scrollAreaRef}>
        <div className="space-y-4">
          {messages.map((message) => (
            <div key={message.id} className="space-y-2">
              <div className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`flex gap-2 max-w-[80%] ${message.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs ${
                    message.role === 'user' 
                      ? 'bg-primary text-primary-foreground' 
                      : 'bg-muted text-muted-foreground'
                  }`}>
                    {message.role === 'user' ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
                  </div>
                  <div className={`rounded-lg p-3 ${
                    message.role === 'user'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-foreground'
                  }`}>
                    <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                    {/* Enhanced Activity Response Parser */}
                    {message.role === 'assistant' && (
                      <ActivityResponseParser
                        content={message.content}
                        onActivityAction={handleActivityAction}
                        userGrade={selectedGrade}
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* Activity Recommendations with Direct Action Buttons */}
              {message.recommendations && message.recommendations.length > 0 && (
                <div className="grid gap-3 mt-4 ml-10">
                  <p className="text-sm font-medium text-muted-foreground">Recommended Activities:</p>
                  {message.recommendations.map((rec, index) => (
                    <Card 
                      key={index} 
                      className="p-4 border-2 hover:border-primary/50 hover:shadow-md transition-all duration-200"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                              rec.type === 'test' ? 'bg-blue-100 text-blue-700' :
                              rec.type === 'game' ? 'bg-green-100 text-green-700' :
                              rec.type === 'worksheet' ? 'bg-purple-100 text-purple-700' :
                              'bg-orange-100 text-orange-700'
                            }`}>
                              {rec.type.toUpperCase()}
                            </span>
                            <h4 className="font-semibold text-sm">{rec.title}</h4>
                          </div>
                          <p className="text-xs text-muted-foreground mb-3">{rec.description}</p>
                          <Button 
                            size="sm" 
                            className="w-full"
                            onClick={() => handleActivityAction(rec)}
                          >
                            <Play className="h-3 w-3 mr-2" />
                            Start Now
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          ))}
          
          {isLoading && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                <Bot className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="bg-muted rounded-lg p-3">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" />
                  <div className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                  <div className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                </div>
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Input */}
      <div className="p-4 border-t">
        <div className="flex gap-2">
          <Input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder={`Ask me about Grade ${selectedGrade} activities, tests, or any topic...`}
            disabled={isLoading}
            className="flex-1"
          />
          <Button 
            onClick={() => {
              console.log('Send button clicked');
              sendMessage();
            }} 
            disabled={!input.trim() || isLoading}
            size="sm"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};
